/* eslint-disable react/no-unescaped-entities */
/**
 * Healing What You Cannot Feel™ — Interactive 12-Module App.
 * Route: /coaching/healing-what-you-cannot-feel/app
 *
 * Distinct visual identity per spec: near-black background, glowing purple
 * accents (used constantly, not sparingly), green reserved for genuine
 * breakthrough moments. Recessed/inset input shadows. Slower transitions
 * (450-600ms). All 5 brand colors present but weighted for depth.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, CheckCircle2, LogOut, ChevronRight, Loader2, Award, Printer, BookOpen } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "hwycf_token";
const EMAIL_KEY = "hwycf_email";
const NAME_KEY = "hwycf_name";
const PROGRAM_SLUG = "healing-what-you-cannot-feel";

const C = {
  bg: "#141014",
  purple: "#5B2C83",
  purpleGlow: "#9B59D9",
  purpleDeep: "#2E1745",
  surface: "#1E1922",
  surfaceAlt: "#262029",
  surfaceRaised: "#2C2530",
  text: "#F2EEF6",
  textDim: "#B7ADC2",
  textFaint: "#7A7284",
  silver: "#C0C0C0",
  green: "#7FA66A",
  greenGlow: "#9CD87E",
  border: "rgba(192,192,192,0.14)",
  borderSoft: "rgba(192,192,192,0.08)",
  shadow: "0 12px 40px rgba(0,0,0,0.45)",
};

const H = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
};
const api = {
  async get(path) { const r = await fetch(`${API}${path}`, { headers: H() }); if (!r.ok) throw new Error(String(r.status)); return r.json(); },
  async patch(path, body) { const r = await fetch(`${API}${path}`, { method: "PATCH", headers: H(), body: JSON.stringify(body) }); if (!r.ok) throw new Error(String(r.status)); return r.json(); },
  async post(path, body) { const r = await fetch(`${API}${path}`, { method: "POST", headers: H(), body: JSON.stringify(body) }); if (!r.ok) throw new Error(String(r.status)); return r.json(); },
};

export default function HWYCFApp() {
  const navigate = useNavigate();
  const [curriculum, setCurriculum] = useState(null);
  const [progress, setProgress] = useState(null);
  const [active, setActive] = useState(1);
  const [tab, setTab] = useState("lesson");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [certOpen, setCertOpen] = useState(false);
  const [bridgeSeen, setBridgeSeen] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) return navigate("/coaching/hwycf/login");
    (async () => {
      try {
        const [c, p] = await Promise.all([
          api.get(`/api/coaching/programs/${PROGRAM_SLUG}/curriculum`),
          api.get(`/api/coaching/programs/${PROGRAM_SLUG}/progress`),
        ]);
        setCurriculum(c); setProgress(p); setActive(p.current_module || 1);
      } catch { navigate("/coaching/hwycf/login"); }
    })();
  }, [navigate]);

  const aC = useMemo(() => curriculum?.modules.find((m) => m.number === active), [curriculum, active]);
  const aP = useMemo(() => progress?.modules.find((m) => m.module_number === active), [progress, active]);

  const wComp = (a) => Array.isArray(a) && a.length === 4 && a.every((x) => (x || "").trim().length >= 5);
  const jComp = (a) => Array.isArray(a) && a.length === 5 && a.every((x) => (x || "").trim().length >= 10);
  const aComp = (t) => typeof t === "string" && t.trim().length >= 20;

  const reload = async () => {
    const p = await api.get(`/api/coaching/programs/${PROGRAM_SLUG}/progress`);
    setProgress(p); return p;
  };
  const schedSave = (fn) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => { setSaving(true); try { await fn(); await reload(); } catch { /* transient */ } setSaving(false); }, 600);
  };

  function pick(n) {
    const mp = progress?.modules.find((x) => x.module_number === n);
    if (!mp?.unlocked) return;
    setActive(n); setTab("lesson");
  }
  function upW(i, v) {
    setProgress((prev) => {
      const next = { ...prev, modules: prev.modules.map((m) => {
        if (m.module_number !== active) return m;
        const a = [...(m.worksheet_answers || ["","","",""])]; a[i] = v;
        return { ...m, worksheet_answers: a };
      }) };
      const answers = next.modules.find((m) => m.module_number === active).worksheet_answers;
      schedSave(() => api.patch(`/api/coaching/programs/${PROGRAM_SLUG}/module/${active}/worksheet`, { answers }));
      return next;
    });
  }
  function upJ(i, v) {
    setProgress((prev) => {
      const next = { ...prev, modules: prev.modules.map((m) => {
        if (m.module_number !== active) return m;
        const a = [...(m.journal_answers || ["","","","",""])]; a[i] = v;
        return { ...m, journal_answers: a };
      }) };
      const answers = next.modules.find((m) => m.module_number === active).journal_answers;
      schedSave(() => api.patch(`/api/coaching/programs/${PROGRAM_SLUG}/module/${active}/journal`, { answers }));
      return next;
    });
  }
  function upA(v) {
    setProgress((prev) => ({ ...prev, modules: prev.modules.map((m) =>
      m.module_number === active ? { ...m, activity_response: v } : m) }));
  }
  async function submit() {
    if (!aP || (aP.activity_response || "").trim().length < 20) return;
    setSubmitting(true);
    try {
      await api.post(`/api/coaching/programs/${PROGRAM_SLUG}/module/${active}/activity`,
                     { response: aP.activity_response.trim() });
      const p = await reload();
      if (p.program_complete && active === 12) setCertOpen(true);
    } catch { /* ignore */ }
    setSubmitting(false);
  }
  function logout() {
    [TOKEN_KEY, EMAIL_KEY, NAME_KEY].forEach((k) => localStorage.removeItem(k));
    navigate("/coaching/hwycf/login");
  }

  if (!curriculum || !progress) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: C.bg }}>
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: C.purpleGlow }} /></div>;
  }

  return (
    <div data-testid="hwycf-app" className="min-h-screen"
         style={{ background: C.bg, color: C.text, fontFamily: "'Inter', sans-serif" }}>
      <header className="sticky top-0 z-20"
              style={{ background: `linear-gradient(180deg,${C.bg} 0%,rgba(20,16,20,0.85) 100%)`,
                       borderBottom: `1px solid ${C.borderSoft}` }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: C.purpleGlow, fontWeight: 700 }}>
              Program 02 · Emotional Healing
            </div>
            <div style={{ fontFamily: "'Playfair Display', serif", color: C.text, fontSize: 15 }}>
              Healing What You Cannot Feel™ · 12 Weeks
            </div>
          </div>
          <div className="flex items-center gap-4 flex-1 max-w-md">
            <div className="flex-1">
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: C.surfaceAlt }}>
                <div data-testid="progress-fill" className="h-full"
                     style={{ width: `${progress.progress_pct}%`,
                              background: `linear-gradient(90deg,${C.purple},${C.purpleGlow})`,
                              boxShadow: `0 0 12px ${C.purpleGlow}55`,
                              transition: "width .6s cubic-bezier(.4,0,.2,1)" }} />
              </div>
              <div style={{ fontSize: 11, color: C.textFaint, marginTop: 4 }} data-testid="progress-label">
                {progress.completed_count} / {progress.total_modules} Modules · {progress.progress_pct}%
              </div>
            </div>
            <button onClick={logout} className="text-xs inline-flex items-center gap-1"
                    style={{ color: C.textFaint }} data-testid="hwycf-logout">
              <LogOut className="w-3 h-3" /> Sign out
            </button>
          </div>
        </div>
      </header>

      {certOpen ? (
        <CertificateScreen progress={progress} onClose={() => setCertOpen(false)} onBridge={() => setBridgeSeen(true)} bridgeSeen={bridgeSeen} />
      ) : (
        <div className="max-w-6xl mx-auto px-6 py-10 grid md:grid-cols-[300px_1fr] gap-7">
          {/* Sidebar */}
          <aside className="md:sticky md:top-28 self-start" data-testid="hwycf-sidebar">
            <div className="rounded-2xl p-3"
                 style={{ background: C.surface, border: `1px solid ${C.border}`, boxShadow: C.shadow }}>
              <div className="text-[10px] uppercase tracking-[0.24em] mb-3 px-2"
                   style={{ color: C.purpleGlow, fontWeight: 700 }}>
                Your 12-week excavation
              </div>
              {progress.modules.map((m) => {
                const cur = curriculum.modules.find((c) => c.number === m.module_number);
                const activeItem = m.module_number === active;
                const clickable = m.unlocked;
                const done = m.completion_status;
                return (
                  <button key={m.module_number} onClick={() => pick(m.module_number)}
                          disabled={!clickable} data-testid={`module-item-${m.module_number}`}
                          className="w-full text-left rounded-xl px-3 py-3 flex items-start gap-3 mb-1"
                          style={{
                            background: activeItem ? "rgba(155,89,217,0.15)" : "transparent",
                            border: activeItem ? `1px solid ${C.purpleGlow}55` : "1px solid transparent",
                            opacity: clickable ? 1 : 0.45,
                            cursor: clickable ? "pointer" : "not-allowed",
                            transition: "background .45s ease, border-color .45s ease",
                          }}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                         style={{
                           background: done ? C.green : clickable ? "rgba(155,89,217,0.15)" : C.surfaceAlt,
                           color: done ? "white" : clickable ? C.purpleGlow : C.textFaint,
                           fontSize: 11, fontWeight: 600,
                           boxShadow: done ? `0 0 12px ${C.green}55` : activeItem ? `0 0 12px ${C.purpleGlow}55` : "none",
                         }}>
                      {done ? <CheckCircle2 className="w-4 h-4" /> : clickable ? m.module_number : <Lock className="w-3 h-3" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wide" style={{ color: C.textFaint }}>
                        Module {m.module_number} · {cur?.book_chapter_tag || ""}
                      </div>
                      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 14, color: C.text, lineHeight: 1.35 }}>
                        {cur?.title || `Week ${m.module_number}`}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Main */}
          <main className="min-w-0" data-testid="hwycf-main">
            {aC && aP && (
              <>
                <div className="rounded-2xl p-7 mb-6"
                     style={{ background: C.surface, border: `1px solid ${C.border}`, boxShadow: C.shadow }}>
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] mb-3"
                       style={{ color: C.purpleGlow, fontWeight: 700 }}>
                    <BookOpen className="w-3 h-3" /> {aC.book_chapter_tag}
                  </div>
                  <h1 className="text-4xl leading-tight"
                      style={{ fontFamily: "'Playfair Display', serif", color: C.text, letterSpacing: "-0.01em" }}>
                    {aC.title}
                  </h1>
                  <p className="mt-3 text-sm leading-relaxed" style={{ color: C.textDim, letterSpacing: "0.01em" }}>
                    <span className="uppercase tracking-widest text-[10px] font-semibold" style={{ color: C.purpleGlow }}>Objective · </span>
                    {aC.objective}
                  </p>
                </div>

                <div className="flex gap-1 mb-6" style={{ borderBottom: `1px solid ${C.border}` }} data-testid="hwycf-tabs">
                  {[
                    ["lesson", "Lesson", true],
                    ["worksheet", "Worksheet", wComp(aP.worksheet_answers)],
                    ["journal", "Journal", jComp(aP.journal_answers)],
                    ["activity", "Activity", aComp(aP.activity_response)],
                  ].map(([k, l, d]) => {
                    const a = tab === k;
                    return (
                      <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                              className="px-5 py-3 text-sm inline-flex items-center gap-2"
                              style={{
                                borderBottom: a ? `2px solid ${C.purpleGlow}` : "2px solid transparent",
                                color: a ? C.purpleGlow : C.textDim,
                                fontWeight: a ? 600 : 400, transition: "all .35s ease",
                              }}>
                        {l}<span className="w-1.5 h-1.5 rounded-full"
                                  style={{ background: d ? C.green : C.textFaint }} />
                      </button>
                    );
                  })}
                </div>

                {tab === "lesson" && (
                  <Panel><div className="mb-6 pl-6 py-3 italic text-lg leading-relaxed"
                                style={{ borderLeft: `3px solid ${C.purpleGlow}`,
                                         fontFamily: "'Playfair Display', serif",
                                         color: C.text, letterSpacing: "0.005em" }} data-testid="lesson-quote">
                    "{aC.reflection_quote}"
                  </div>
                  <p style={{ color: C.textDim, lineHeight: 1.85, letterSpacing: "0.005em" }} data-testid="lesson-body">
                    {aC.coaching_body}
                  </p></Panel>
                )}

                {tab === "worksheet" && (
                  <Panel><div className="text-xs mb-4" style={{ color: C.textFaint }}>
                    Answer each with a few sentences (minimum ~5 characters).
                  </div>{aC.worksheet_prompts.map((q, i) => (
                    <Field key={i} label={q} testId={`worksheet-input-${i}`}
                           value={(aP.worksheet_answers || [])[i] || ""}
                           onChange={(v) => upW(i, v)} minH={90} />
                  ))}<Saving saving={saving} /></Panel>
                )}

                {tab === "journal" && (
                  <Panel><div className="text-xs mb-4" style={{ color: C.textFaint }}>
                    Five daily prompts (minimum ~10 characters each).
                  </div>{aC.journal_prompts.map((q, i) => (
                    <Field key={i} label={`Day ${i + 1} · ${q}`} testId={`journal-input-${i}`}
                           value={(aP.journal_answers || [])[i] || ""}
                           onChange={(v) => upJ(i, v)} minH={90} />
                  ))}<Saving saving={saving} /></Panel>
                )}

                {tab === "activity" && (
                  <Panel>
                    <div className="rounded-xl p-5 mb-5"
                         style={{ background: "rgba(155,89,217,0.09)", borderLeft: `3px solid ${C.purpleGlow}` }}>
                      <div className="text-[10px] uppercase tracking-widest font-semibold mb-2"
                           style={{ color: C.purpleGlow }}>This week's activity</div>
                      <p style={{ color: C.text, lineHeight: 1.75 }} data-testid="activity-prompt">{aC.activity_prompt}</p>
                    </div>
                    <Field label="Your response" testId="activity-input"
                           value={aP.activity_response || ""} onChange={upA}
                           placeholder="What did you do? What came up for you?" minH={180} />
                    <div className="text-xs mt-2" style={{ color: C.textFaint }}>
                      Minimum ~20 characters. When you submit, Dr. Christy will respond with a coaching reflection.
                    </div>
                    <button onClick={submit}
                            disabled={submitting || (aP.activity_response || "").trim().length < 20}
                            data-testid="submit-activity-btn"
                            className="mt-4 rounded-full px-6 py-3 text-sm font-medium text-white disabled:opacity-40 inline-flex items-center gap-2"
                            style={{ background: `linear-gradient(135deg,${C.purple},${C.purpleGlow})`,
                                     boxShadow: `0 0 24px rgba(155,89,217,0.35)`, transition: "all .45s ease" }}>
                      {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Reflecting…</> :
                       aP.ai_feedback ? "Update my response" : "Submit for coaching feedback"}
                      <ChevronRight className="w-4 h-4" />
                    </button>

                    {aP.ai_feedback && (
                      <FeedbackCard
                        feedback={aP.coach_override_feedback || aP.ai_feedback}
                        overridden={!!aP.coach_override_feedback}
                        source={aP.ai_feedback.source} />
                    )}

                    {aP.completion_status && active < 12 && (
                      <div className="mt-5 rounded-xl p-4 flex items-center justify-between"
                           style={{ background: "rgba(127,166,106,0.12)", border: `1px solid ${C.green}55` }}>
                        <div className="text-sm" style={{ color: C.greenGlow }}>
                          <strong>Module complete.</strong> Module {active + 1} is unlocked.
                        </div>
                        <button onClick={() => { setActive(active + 1); setTab("lesson"); }}
                                data-testid="advance-next-module-btn"
                                className="rounded-full px-4 py-2 text-xs font-medium text-white"
                                style={{ background: C.green, boxShadow: `0 0 16px ${C.green}55` }}>
                          Continue <ChevronRight className="w-3 h-3 inline" />
                        </button>
                      </div>
                    )}
                    {aP.completion_status && active === 12 && (
                      <button onClick={() => setCertOpen(true)}
                              data-testid="open-certificate-btn"
                              className="mt-5 rounded-full px-6 py-3 text-sm font-medium text-white inline-flex items-center gap-2"
                              style={{ background: C.green, boxShadow: `0 0 24px ${C.green}55` }}>
                        <Award className="w-4 h-4" /> View my Emotional Healing Certificate
                      </button>
                    )}
                  </Panel>
                )}
              </>
            )}
          </main>
        </div>
      )}
    </div>
  );
}

function Panel({ children }) {
  return <div className="rounded-2xl p-7"
              style={{ background: C.surface, border: `1px solid ${C.border}`, boxShadow: C.shadow }}>{children}</div>;
}

function Field({ label, value, onChange, testId, minH = 90, placeholder }) {
  return (
    <div className="mb-4">
      <label className="block text-sm mb-2" style={{ color: C.text }}>{label}</label>
      <textarea data-testid={testId} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none"
        style={{
          minHeight: minH, background: C.surfaceAlt, color: C.text,
          border: `1px solid ${C.borderSoft}`,
          boxShadow: "inset 0 2px 8px rgba(0,0,0,0.4)",
          transition: "border-color .45s ease, box-shadow .45s ease",
        }}
        onFocus={(e) => { e.target.style.borderColor = C.purpleGlow; e.target.style.boxShadow = `inset 0 2px 8px rgba(0,0,0,0.4), 0 0 0 3px rgba(155,89,217,0.15)`; }}
        onBlur={(e) => { e.target.style.borderColor = C.borderSoft; e.target.style.boxShadow = "inset 0 2px 8px rgba(0,0,0,0.4)"; }} />
    </div>
  );
}

function Saving({ saving }) {
  return <div className="text-xs mt-2" style={{ color: C.textFaint }}>
    {saving ? "Saving…" : "Saved automatically as you write."}
  </div>;
}

function FeedbackCard({ feedback, overridden, source }) {
  return (
    <div className="mt-6 rounded-2xl p-7" data-testid="ai-feedback-card"
         style={{ background: C.surfaceRaised, border: `1px solid rgba(155,89,217,0.35)`,
                  boxShadow: `0 0 30px rgba(91,44,131,0.25)` }}>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold"
             style={{ background: `linear-gradient(155deg,${C.purple},${C.purpleDeep})`,
                      fontFamily: "'Playfair Display', serif",
                      boxShadow: `0 0 16px ${C.purpleGlow}55` }}>CV</div>
        <div>
          <div className="text-sm font-semibold" style={{ color: C.purpleGlow, letterSpacing: "0.02em" }}>
            Dr. Christy Vonta
          </div>
          <div className="text-[11px]" style={{ color: C.textFaint }}>
            {overridden ? "Personal note from your coach" :
             source === "ai" ? "Coaching reflection" : "From Dr. Christy's library"}
          </div>
        </div>
      </div>
      {[["REFLECTION", feedback.reflection], ["OBSERVATION", feedback.observation],
        ["REFRAME", feedback.reframe], ["ENCOURAGEMENT", feedback.encouragement]].map(([l, v]) => (
        <div key={l} className="mb-4">
          <div className="text-[10px] uppercase tracking-widest font-semibold mb-2"
               style={{ color: C.purpleGlow }}>{l}</div>
          <div style={{ color: C.textDim, lineHeight: 1.85, fontSize: 14.5 }}>{v}</div>
        </div>
      ))}
      <div>
        <div className="text-[10px] uppercase tracking-widest font-semibold mb-2"
             style={{ color: C.greenGlow }}>NEXT ACTION</div>
        <div style={{ color: C.textDim, lineHeight: 1.85, fontSize: 14.5 }}>{feedback.next_action}</div>
      </div>
    </div>
  );
}

function CertificateScreen({ progress, onClose, onBridge, bridgeSeen }) {
  const [name, setName] = useState(localStorage.getItem(NAME_KEY) || "");
  const [showBridge, setShowBridge] = useState(false);

  async function confirmAndPrint() {
    if (!name.trim()) return;
    try {
      await api.post(`/api/coaching/programs/${PROGRAM_SLUG}/certificate/confirm-name`,
                     { display_name: name.trim() });
      localStorage.setItem(NAME_KEY, name.trim());
      setTimeout(() => window.print(), 400);
    } catch { window.print(); }
  }

  if (showBridge) return <BridgeScreen onClose={onClose} />;

  return (
    <div className="max-w-3xl mx-auto px-6 py-14" data-testid="hwycf-certificate-screen">
      <button onClick={onClose} className="text-xs mb-6"
              style={{ color: C.textFaint }} data-testid="cert-back-btn">← Back to program</button>
      <div className="rounded-3xl p-12 text-center print:border-4"
           style={{
             background: `radial-gradient(600px 300px at 50% 0%, rgba(155,89,217,0.18), transparent 65%), ${C.surface}`,
             border: `4px double rgba(155,89,217,0.4)`,
             fontFamily: "'Playfair Display', serif",
             boxShadow: `0 0 60px rgba(155,89,217,0.25)`,
           }} data-testid="hwycf-certificate-doc">
        <div className="text-[11px] uppercase tracking-[0.4em]"
             style={{ color: C.purpleGlow, fontWeight: 700 }}>
          Christy Vonta Coaching &amp; Consulting · KCIS Coaching Door
        </div>
        <div className="my-8">
          <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center"
               style={{ background: `linear-gradient(155deg,${C.purple},${C.purpleGlow})`,
                        boxShadow: `0 0 32px ${C.purpleGlow}66` }}>
            <Award className="w-8 h-8 text-white" />
          </div>
        </div>
        <div className="text-sm uppercase tracking-widest mb-2" style={{ color: C.textFaint }}>
          Emotional Healing Completion Certificate
        </div>
        <div className="text-xs" style={{ color: C.textFaint }}>This certifies that</div>
        <input data-testid="cert-name-input" value={name}
               onChange={(e) => setName(e.target.value)}
               placeholder="Your full name"
               className="mt-4 text-3xl border-b-2 focus:outline-none text-center py-2 bg-transparent min-w-[280px]"
               style={{ color: C.text, borderBottomColor: "rgba(155,89,217,0.35)" }} />
        <div className="text-sm mt-6" style={{ color: C.textDim }}>has completed all 12 modules of</div>
        <div className="text-2xl mt-2" style={{ color: C.purpleGlow }}>Healing What You Cannot Feel™</div>
        <div className="text-xs mt-1" style={{ color: C.textFaint }}>
          Program 02 · The Healing Trilogy · 12-week Emotional Healing Journey
        </div>
        <div className="mt-14">
          <div className="text-2xl italic" style={{ color: C.purpleGlow }}>Dr. Christy Vonta</div>
          <div className="text-[10px] uppercase tracking-widest mt-1" style={{ color: C.textFaint }}>
            Founder · Christy Vonta Coaching &amp; Consulting
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-center gap-3 print:hidden">
        <button onClick={confirmAndPrint} disabled={!name.trim()}
                data-testid="cert-print-btn"
                className="rounded-full px-6 py-3 text-sm font-medium text-white inline-flex items-center gap-2 disabled:opacity-40"
                style={{ background: `linear-gradient(135deg,${C.purple},${C.purpleGlow})`,
                         boxShadow: `0 0 24px rgba(155,89,217,0.5)` }}>
          <Printer className="w-4 h-4" /> Confirm &amp; download
        </button>
        {!bridgeSeen && (
          <button onClick={() => setShowBridge(true)}
                  data-testid="cert-see-bridge-btn"
                  className="rounded-full px-6 py-3 text-sm font-medium border"
                  style={{ borderColor: C.greenGlow, color: C.greenGlow }}>
            Continue → what's next
          </button>
        )}
      </div>
    </div>
  );
}

// Bridge screen shown after HWYCF cert. This is Bridge 2 (after HWYCF)
// per the user's locked copy — but since HWYCF is Program 02, the bridge
// is between HWYCF and She Is. Batch A includes Bridge 1 (after Me I See)
// on the me-i-see certificate screen (added separately). This Bridge 2
// component lives here in Batch A ready for use, but the She Is app
// is Batch B — so we show the copy and route to /coaching/dashboard.
function BridgeScreen({ onClose }) {
  return (
    <div className="max-w-3xl mx-auto px-6 py-14" data-testid="hwycf-bridge-screen">
      <button onClick={onClose} className="text-xs mb-6"
              style={{ color: C.textFaint }} data-testid="bridge-back-btn">← Back to certificate</button>
      <div className="rounded-3xl p-10 sm:p-14"
           style={{ background: C.surface, border: `1px solid ${C.border}`, boxShadow: C.shadow }}>
        <div className="text-[11px] uppercase tracking-[0.35em] mb-4"
             style={{ color: C.greenGlow, fontWeight: 700 }}>
          Bridge · Program 02 → Program 03
        </div>
        <h2 className="text-3xl sm:text-4xl leading-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif", color: C.text, letterSpacing: "-0.005em" }}>
          You Opened the Bag. Now It's Time to Rise.
        </h2>
        <div className="space-y-4 leading-relaxed" style={{ color: C.textDim, letterSpacing: "0.005em" }}>
          <p>
            Twelve weeks ago, you agreed to go back for what you'd buried — the grief you deferred, the triggers you never traced to their root, the emotions you'd gotten so skilled at avoiding you almost forgot you were avoiding them. You did that. You built a nervous system toolkit. You wrote letters to what you lost and what you're releasing. You are no longer carrying what you were carrying at Week 1.
          </p>
          <p>But healing was never the finish line either. It was the clearing.</p>
          <p>
            She Is is where you stop processing and start living — where everything you excavated becomes the ground you stand on, not the weight you still manage. This phase matters exactly as much as the one you just completed, because healing without embodiment just becomes another thing you privately did. This is where it becomes how you actually show up.
          </p>
          <p style={{ color: C.text, fontFamily: "'Playfair Display', serif", fontSize: 20, fontStyle: "italic" }}>
            You didn't heal to stay quiet about it.
          </p>
        </div>
        <div className="mt-8">
          <a href="/coaching/dashboard" data-testid="bridge-continue-btn">
            <button className="rounded-full px-6 py-3 text-sm font-medium text-white inline-flex items-center gap-2"
                    style={{ background: `linear-gradient(135deg,${C.purple},${C.greenGlow})`,
                             boxShadow: `0 0 24px rgba(127,166,106,0.4)` }}>
              Begin She Is <ChevronRight className="w-4 h-4" />
            </button>
          </a>
          <div className="mt-3 text-xs" style={{ color: C.textFaint }}>
            She Is is now unlocked in your dashboard.
          </div>
        </div>
      </div>
    </div>
  );
}
